import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';
import { useDraggable } from '../../hooks/useDraggable';

interface InactivityLockModalProps {
  isLocked: boolean;
  onUnlock: () => void;
  inactivityMinutes?: number;
  logoutAfterLockMinutes?: number;
}

export default function InactivityLockModal({
  isLocked,
  onUnlock,
  inactivityMinutes = 5,
  logoutAfterLockMinutes = 5,
}: InactivityLockModalProps) {
  const { user, logout, setUnlockingSession } = useAuth();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [maxAttempts, setMaxAttempts] = useState(5);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [accountBlocked, setAccountBlocked] = useState(false);
  const [autoLogoutCountdown, setAutoLogoutCountdown] = useState(0);

  // Fetch max attempts from parametres_systeme
  useEffect(() => {
    const fetchMaxAttempts = async () => {
      try {
        const { data } = await supabase
          .from('parametres_systeme')
          .select('valeur')
          .eq('cle', 'max_tentatives_deverrouillage')
          .maybeSingle();
        if (data?.valeur) {
          setMaxAttempts(Math.max(1, parseInt(data.valeur, 10) || 5));
        }
      } catch {
        // Use default
      }
    };
    fetchMaxAttempts();
  }, []);

  // Auto-logout countdown when locked
  useEffect(() => {
    if (!isLocked) {
      setAutoLogoutCountdown(0);
      return;
    }

    const totalSeconds = logoutAfterLockMinutes * 60;
    setAutoLogoutCountdown(totalSeconds);

    const interval = setInterval(() => {
      setAutoLogoutCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isLocked, logoutAfterLockMinutes]);

  // Reset state when the modal is closed
  useEffect(() => {
    if (!isLocked) {
      setPassword('');
      setError('');
      setAttempts(0);
      setLockedUntil(null);
      setAccountBlocked(false);
      setAutoLogoutCountdown(0);
    }
  }, [isLocked]);

  // Countdown timer for temporary block
  useEffect(() => {
    if (lockedUntil === null) return;

    const interval = setInterval(() => {
      const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockedUntil(null);
        setCountdown(0);
        setAttempts(0);
        clearInterval(interval);
      } else {
        setCountdown(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lockedUntil]);

  const notifyAdminsOfBlock = useCallback(async () => {
    if (!user) return;
    try {
      // Create a notification for all admins
      const { data: admins } = await supabase
        .from('users')
        .select('id')
        .eq('role', 'admin')
        .eq('statut', 'actif');

      if (admins && admins.length > 0) {
        const notifications = admins.map(admin => ({
          user_id: admin.id,
          type: 'securite',
          titre: 'Compte bloqué — tentatives de déverrouillage',
          message: `Le compte de ${user.prenom} ${user.nom} (${user.email}) a été temporairement bloqué après ${maxAttempts} tentatives échouées de déverrouillage de session.`,
          lien: '/gestion-utilisateurs',
          created_at: new Date().toISOString(),
        }));

        await supabase.from('notifications').insert(notifications);
      }

      // Log in audit
      await supabase.from('audit_logs').insert({
        action: 'blocage_compte_inactivite',
        entite: 'users',
        entite_id: user.id,
        details: {
          raison: `${maxAttempts} tentatives échouées de déverrouillage`,
          email: user.email,
          nom: `${user.prenom} ${user.nom}`,
        },
        created_at: new Date().toISOString(),
      });
    } catch (err) {
      logger.error('Erreur notification admin:', err);
    }
  }, [user, maxAttempts]);

  const { dragStyle, dragHandleProps } = useDraggable(isLocked);

  if (!isLocked) return null;

  const isTemporarilyBlocked =
    lockedUntil !== null && Date.now() < lockedUntil;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email || isTemporarilyBlocked || accountBlocked) return;

    setError('');
    setLoading(true);

    try {
      // Signaler qu'on est en train de déverrouiller AVANT l'appel auth
      // pour que l'événement SIGNED_IN soit ignoré par AuthContext
      setUnlockingSession(true);

      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      });

      if (authError) {
        // En cas d'erreur, relâcher le flag immédiatement
        setUnlockingSession(false);
        throw new Error('Mot de passe incorrect');
      }

      setPassword('');
      setAttempts(0);

      // Déverrouiller la session
      onUnlock();

      // Relâcher le flag après 2s (laisser les événements auth se stabiliser)
      setTimeout(() => {
        setUnlockingSession(false);
      }, 2000);

    } catch {
      setUnlockingSession(false);
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPassword('');

      if (newAttempts >= maxAttempts) {
        setLockedUntil(Date.now() + 2 * 60 * 1000);
        setAccountBlocked(true);
        setError(`Compte temporairement bloqué après ${maxAttempts} tentatives échouées. Les administrateurs ont été notifiés.`);
        // Notify admins
        notifyAdminsOfBlock();
      } else {
        setError(
          `Mot de passe incorrect. ${maxAttempts - newAttempts} tentative(s) restante(s).`,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      logger.error('Erreur lors de la déconnexion :', err);
    }
  };

  const lockedAt = new Date();
  const timeStr = lockedAt.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop flouté */}
      <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm" />

      {/* Modale */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" style={dragStyle}>
        {/* Bande supérieure */}
        <div className={`px-8 py-6 text-white text-center ${accountBlocked ? 'bg-gradient-to-r from-red-600 to-red-700' : 'bg-gradient-to-r from-teal-600 to-teal-700'}`} {...dragHandleProps}>
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${accountBlocked ? 'bg-white/20' : 'bg-white/20'}`}>
            <i className={`text-3xl text-white ${accountBlocked ? 'ri-shield-keyhole-line' : 'ri-lock-2-line'}`}></i>
          </div>
          <h2 className="text-xl font-bold">
            {accountBlocked ? 'Compte temporairement bloqué' : 'Session verrouillée'}
          </h2>
          <p className={`text-sm mt-1 ${accountBlocked ? 'text-red-100' : 'text-teal-100'}`}>
            {accountBlocked
              ? `Trop de tentatives échouées — administrateurs notifiés`
              : `Inactivité détectée à ${timeStr}`}
          </p>
        </div>

        {/* Corps */}
        <div className="px-8 py-6">
          {/* Auto-logout warning */}
          {autoLogoutCountdown > 0 && (
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 mb-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 animate-pulse">
                  <i className="ri-time-line text-orange-600 text-lg"></i>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-orange-900 mb-1">
                    Déconnexion automatique dans
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-orange-600 tabular-nums">
                      {formatTime(autoLogoutCountdown)}
                    </span>
                    <span className="text-xs text-orange-600">minutes</span>
                  </div>
                  <p className="text-xs text-orange-700 mt-2">
                    Déverrouillez maintenant pour éviter la déconnexion automatique
                  </p>
                  {/* Progress bar */}
                  <div className="mt-3 w-full h-2 bg-orange-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-1000 ease-linear"
                      style={{ width: `${(autoLogoutCountdown / (logoutAfterLockMinutes * 60)) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Infos utilisateur */}
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 mb-6">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-teal-600 text-lg"></i>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {user?.prenom} {user?.nom}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>

          {accountBlocked && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
              <div className="flex items-start gap-2">
                <i className="ri-alarm-warning-fill text-red-500 mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-red-800">Compte bloqué</p>
                  <p className="text-xs text-red-600 mt-1">
                    Après {maxAttempts} tentatives échouées, votre compte a été temporairement bloqué.
                    Un email de notification a été envoyé aux administrateurs.
                    Veuillez patienter ou vous déconnecter.
                  </p>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-gray-600 mb-5 text-center">
            {accountBlocked
              ? 'Vous pouvez réessayer après le délai de blocage ou vous déconnecter.'
              : `Votre session a été verrouillée après ${inactivityMinutes} minute${inactivityMinutes > 1 ? 's' : ''} d'inactivité. Saisissez votre mot de passe pour reprendre.`}
          </p>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && !accountBlocked && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {isTemporarilyBlocked && !accountBlocked && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-2">
                <i className="ri-time-line text-orange-500 flex-shrink-0"></i>
                <p className="text-sm text-orange-700">
                  Réessayez dans{' '}
                  <span className="font-bold">{countdown}s</span>
                </p>
              </div>
            )}

            {accountBlocked && isTemporarilyBlocked && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-2">
                <i className="ri-time-line text-orange-500 flex-shrink-0"></i>
                <p className="text-sm text-orange-700">
                  Déblocage dans{' '}
                  <span className="font-bold">{countdown}s</span>
                </p>
              </div>
            )}

            {/* Barre de tentatives */}
            {!accountBlocked && attempts > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Tentatives</span>
                  <span className={`font-semibold ${attempts >= maxAttempts - 1 ? 'text-red-600' : 'text-amber-600'}`}>
                    {attempts} / {maxAttempts}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      attempts >= maxAttempts - 1 ? 'bg-red-500' : 'bg-amber-500'
                    }`}
                    style={{ width: `${(attempts / maxAttempts) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isTemporarilyBlocked || loading}
                  autoFocus
                  className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i
                    className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}
                  ></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!password || loading || isTemporarilyBlocked}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors whitespace-nowrap"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Vérification...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line"></i>
                  Déverrouiller
                </>
              )}
            </button>
          </form>

          {/* Séparateur */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-400">ou</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Déconnexion */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-line text-gray-500"></i>
            Se déconnecter
          </button>
        </div>

        {/* Pied de page */}
        <div className="bg-gray-50 px-8 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
            <i className="ri-shield-check-line text-teal-500"></i>
            Verrouillage après {inactivityMinutes} min • Déconnexion après {logoutAfterLockMinutes} min
          </p>
        </div>
      </div>
    </div>
  );
}